import { Box } from "@chakra-ui/layout";
import { Modal } from "antd";
import useSavePastaGeral from "core/features/documentos/hooks/useSavePasta";
import { PastaForm, UseSavePastaProps } from "core/features/documentos/typings";
import { showErrorAxios, showSuccess } from "infra/helpers/alerta";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import FooterModal from "ui/components/Button/FooterModal";
import Field from "components/Input/Field";
import { AxiosResponse } from "axios";
import { UseMutationResult } from "react-query";
import { Col, Row } from "antd";
import SelectTipoDestino from "ui/components/Input/SelectTipoDestino";
import { TipoDestinoEnum } from "core/features/documentos/enums";
import SelectGrupoUnidade from "ui/components/Input/SelectGrupoUnidade";
import SelectUnidade from "ui/components/Input/SelectUnidade";
import { useCurrentTenant } from "core/features/auth/tenant.store";

interface ModalPastaProps {
  isOpen: boolean;
  onClose: () => any;
  onSave: () => any;
  pastaId?: string;
  pasta?:
    | {
        nome: string;
        id: string;
        tipoDestinoId?: TipoDestinoEnum | null | undefined;
        destinoId?: string | undefined;
      }
    | undefined;
  unidadeId?: string;
  documentoPaiId?: string;
  useSavePasta?: (
    props: UseSavePastaProps
  ) => UseMutationResult<AxiosResponse<any>, unknown, PastaForm, unknown>;
}

function ModalPasta({
  isOpen,
  onClose,
  pastaId,
  onSave,
  pasta,
  unidadeId,
  useSavePasta = useSavePastaGeral,
  documentoPaiId,
}: ModalPastaProps) {
  const [registerAnother, setRegisterAnother] = useState(false);
  const { isLoading, mutate: handleSave } = useSavePasta({
    id: pastaId,
    unidadeId,
  });

  const defaultValues = {
    nome: "",
    destinoId: "",
    tipoDestinoId: undefined,
  };

  const { tenant } = useCurrentTenant();
  const { control, reset, formState, handleSubmit, watch } = useForm<PastaForm>(
    {
      mode: "all",
      defaultValues: {
        ...defaultValues,
      },
    }
  );

  const form = watch();

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (pasta) {
      reset({
        nome: pasta.nome,
        destinoId: pasta.destinoId,
        tipoDestinoId: pasta.tipoDestinoId,
      });
    }
  }, [pasta, reset]);

  const onSubmit = async (data: PastaForm) => {
    handleSave(
      { ...data, documentoPaiId },
      {
        onSuccess: async () => {
          const { isConfirmed } = await showSuccess();

          if (isConfirmed) {
            onSave();
            reset({
              nome: "",
              destinoId: "",
              tipoDestinoId: undefined,
              files: [],
            });
            if (!registerAnother) {
              onClose();
            }
          }
        },
        onError: (err) => {
          showErrorAxios(err);
        },
      }
    );
  };

  return (
    <Modal
      centered
      visible={isOpen}
      footer={
        <FooterModal
          isDisabled={!formState.isValid}
          onSubmit={() => {
            handleSubmit(onSubmit)();
          }}
          showRegisterAnother={!pastaId}
          registerAnother={registerAnother}
          setRegisterAnother={setRegisterAnother}
          isLoadingSave={isLoading!}
          onClose={onClose}
        />
      }
      title={(pastaId ? "Editar" : "Criar") + " pasta"}
      destroyOnClose
      onCancel={onClose}
      closable={true}
      width="600px"
    >
      <Box mb={8}>
        <Box as={Row} gutter={16} mb={4}>
          <Box as={Col} xs={24}>
            <Field
              control={control}
              name="nome"
              label="Nome da pasta"
              rules={{ required: true }}
            />
          </Box>
        </Box>

        <Box as={Row} gutter={16}>
          <Box as={Col} xs={12}>
            <Field
              shouldUnregister
              control={control}
              label="Enviar para quem"
              name="tipoDestinoId"
              render={({ field }) => (
                <SelectTipoDestino
                  onChange={field.onChange}
                  value={field.value}
                  showEmail={false}
                  allowClear
                />
              )}
            />
          </Box>
          {form.tipoDestinoId &&
          form.tipoDestinoId !== TipoDestinoEnum.TodasUnidades ? (
            <Box as={Col} xs={12}>
              <Field
                shouldUnregister
                control={control}
                label={
                  form.tipoDestinoId === TipoDestinoEnum.UmGrupoUnidades
                    ? "Grupo"
                    : "Unidade"
                }
                name="destinoId"
                render={({ field, isInvalid }) =>
                  form.tipoDestinoId === TipoDestinoEnum.UmGrupoUnidades ? (
                    <SelectGrupoUnidade
                      onChange={field.onChange}
                      isInvalid={isInvalid}
                      value={field.value}
                    />
                  ) : (
                    <SelectUnidade
                      tenantId={tenant.id}
                      onChange={field.onChange}
                      isInvalid={isInvalid}
                      value={field.value}
                    />
                  )
                }
              />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Modal>
  );
}

export default ModalPasta;
